import React, { useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Carousel } from 'react-responsive-carousel';
import './HomeMan.scss';

/* ---------- RowCarousel reutilizable ---------- */
const RowCarousel = ({ items = [], renderItem, ariaLabel, itemMinWidth = 220 }) => {
  const trackRef = useRef(null);
  const scrollByDir = (dir) => {
    const node = trackRef.current;
    if (!node) return;
    const amount = Math.max(node.clientWidth * 0.9, itemMinWidth * 1.5);
    node.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };
  if (!items?.length) return null;
  return (
    <div className="hm-row" role="region" aria-label={ariaLabel}>
      <button type="button" className="hm-row__nav hm-row__nav--prev" aria-label="Anterior" onClick={() => scrollByDir(-1)}>‹</button>
      <div className="hm-row__track" ref={trackRef} style={{ ['--hm-item-min']: `${itemMinWidth}px` }}>
        {items.map((it, idx) => (
          <div className="hm-row__item" key={it?.id ?? idx}>
            {renderItem(it)}
          </div>
        ))}
      </div>
      <button type="button" className="hm-row__nav hm-row__nav--next" aria-label="Siguiente" onClick={() => scrollByDir(1)}>›</button>
    </div>
  );
};

/* ---------- helpers ---------- */
const getCatCode = (it) =>
  it?.category?.code ?? it?.category_code ?? it?.categoryCode ?? it?.codeCategory ?? it?.code;
const getSubCode = (it) =>
  it?.subcategory?.code ?? it?.subCategory?.code ?? it?.subcategory_code ?? it?.subCategoryCode;
const getBannerImage = (it) => it?.image ?? it?.imageHome ?? it?.banner ?? it?.url ?? '';
const getBannerImageMobile = (it) => it?.imageMobile ?? getBannerImage(it);
const getCategoryImage = (c) =>
  c?.imageHome ?? c?.image_home ?? c?.image ?? c?.thumbnail ?? c?.banner ?? '';

/* ---------- Componente principal ---------- */
const HomeMan = ({
  cartel = [],
  sliders = [],
  categories = [],
  categoriesHome = [],
  mostSelled = [],
  featuredProducts = [],
  offers = [],
  marcasHome = [],
  renderItem,
  renderBrand,
  submitNewsletter,
  setNewsletter,
}) => {
  // offset del header fijo
  useEffect(() => {
    const setHeaderOffset = () => {
      const topbar = document.querySelector('.top-nav, .topbar, .top-nav-container, .header-top, .header__top');
      const navbar = document.querySelector('.main-nav-container-sandy, .main-nav-container');
      const total = (topbar?.offsetHeight || 0) + (navbar?.offsetHeight || 0);
      const fallback = 88;
      document.documentElement.style.setProperty('--header-total', `${total || fallback}px`);
    };
    setHeaderOffset();
    window.addEventListener('resize', setHeaderOffset);
    return () => window.removeEventListener('resize', setHeaderOffset);
  }, []);

  // categorías planas
  const flatCategories = useMemo(() => {
    if (categories?.length) return categories;
    if (categoriesHome?.length) return categoriesHome.map((ch) => ch?.category || ch).filter(Boolean);
    return [];
  }, [categories, categoriesHome]);

  // Solo categorías con imagen
  const categoriesWithImage = useMemo(
    () => flatCategories.filter((c) => !!getCategoryImage(c)),
    [flatCategories]
  );

  const goCartel = (i) => {
    const it = cartel?.[i];
    if (!it) return '/productos';
    const cat = getCatCode(it);
    const sub = getSubCode(it);
    if (cat && sub) return `/productos/${cat}/${sub}`;
    if (cat) return `/productos/${cat}`;
    return '/productos';
  };

  const CATGRID_COUNT = 3;

  return (
    <div className="home-man">
      {/* HERO */}
      {!!sliders?.length && (
        <section className="hm-hero">
          <div className="hm-hero__inner">
            <Carousel
              autoPlay
              infiniteLoop
              showThumbs={false}
              showStatus={false}
              emulateTouch
              swipeable
              dynamicHeight
            >
              {sliders.map(({ id, image, isImage, text }) => (
                <div key={id} className="hm-hero__slide">
                  {isImage !== false ? (
                    <img src={image} alt={text || 'Portada'} />
                  ) : (
                    <video autoPlay muted loop playsInline aria-label={text || 'Video'}>
                      <source src={image} type="video/mp4" />
                    </video>
                  )}
                </div>
              ))}
            </Carousel>
          </div>
        </section>
      )}

      {/* GRID CATEGORÍAS CON FOTO */}
      {!!categoriesWithImage.length && (
        <section className="container hm-catgrid">
          {categoriesWithImage.slice(0, CATGRID_COUNT).map((c) => (
            <Link key={c.id ?? c.code ?? c.name} to={`/productos/${c.code}`} className="hm-catgrid__card">
              <div className="hm-catgrid__media">
                <img src={getCategoryImage(c)} alt={c.name} />
              </div>
              <div className="hm-catgrid__caption"><span>{c.name}</span></div>
            </Link>
          ))}
        </section>
      )}

      {/* NAV CATEGORÍAS (chips) */}
      {!!flatCategories?.length && (
        <section className="hm-catnav container">
          <div className="hm-catnav__track" role="navigation" aria-label="Categorías">
            {flatCategories.map((c) => (
              <Link key={c.id ?? c.code ?? c.name} to={`/productos/${c.code}`} className="hm-chip">
                {c.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* BANNER FULL BLEED */}
      {cartel[0] && (
        <section className="hm-banner hm-banner--full">
          <picture>
            <source media="(max-width: 768px)" srcSet={getBannerImageMobile(cartel[0])} />
            <img className="hm-banner__img" src={getBannerImage(cartel[0])} alt="" />
          </picture>
          <Link className="hm-banner__cta" to={goCartel(0)}>Ver colección</Link>
        </section>
      )}

      {/* NOVEDADES */}
      {!!featuredProducts?.length && (
        <section className="container hm-rail">
          <header className="hm-rail__head">
            <h2>NOVEDADES</h2>
            <Link to="/productos" className="hm-rail__link">Ver todo</Link>
          </header>
          <RowCarousel items={featuredProducts} renderItem={renderItem} ariaLabel="Novedades" itemMinWidth={220} />
        </section>
      )}

      {/* OFERTAS (nuevo) */}
      {!!offers?.length && (
        <section className="container hm-rail">
          <header className="hm-rail__head">
            <h2>OFERTAS</h2>
            <Link to="/ofertas" className="hm-rail__link">Ver todas</Link>
          </header>
          <RowCarousel items={offers} renderItem={renderItem} ariaLabel="Ofertas" itemMinWidth={220} />
        </section>
      )}

      {/* GRID 2-UP DE CAMPAÑA */}
      {(cartel[1] || cartel[2]) && (
        <section className="hm-promo2">
          {cartel.slice(1, 3).map((b, i) => (
            !!b && (
              <Link key={i} to={goCartel(1 + i)} className="hm-promo2__card">
                <picture>
                  <source media="(max-width: 768px)" srcSet={getBannerImageMobile(b)} />
                  <img src={getBannerImage(b)} alt="" />
                </picture>
              </Link>
            )
          ))}
        </section>
      )}

      {/* MÁS VENDIDOS */}
      {!!mostSelled?.length && (
        <section className="container hm-rail">
          <header className="hm-rail__head">
            <h2>MÁS VENDIDOS</h2>
            <Link to="/productos" className="hm-rail__link">Ver todo</Link>
          </header>
          <RowCarousel items={mostSelled} renderItem={renderItem} ariaLabel="Más vendidos" itemMinWidth={220} />
        </section>
      )}

      {/* MARCAS */}
      {!!marcasHome?.length && (
        <section className="container hm-brands">
          <header className="hm-rail__head">
            <h2>MARCAS</h2>
          </header>
          <RowCarousel
            items={marcasHome}
            ariaLabel="Marcas"
            itemMinWidth={140}
            renderItem={(brand) =>
              renderBrand ? (
                renderBrand(brand)
              ) : (
                <a className="hm-brand" href={brand?.url || '#'} target={brand?.url ? '_blank' : '_self'} rel="noreferrer" aria-label={brand?.name || 'Marca'}>
                  <img src={brand?.image || brand?.logo} alt={brand?.name || ''} />
                </a>
              )
            }
          />
        </section>
      )}

      {/* BANNER FINAL */}
      {cartel[3] && (
        <section className="hm-banner hm-banner--full">
          <picture>
            <source media="(max-width: 768px)" srcSet={getBannerImageMobile(cartel[3])} />
            <img className="hm-banner__img" src={getBannerImage(cartel[3])} alt="" />
          </picture>
          <Link className="hm-banner__cta" to={goCartel(3)}>Ver colección</Link>
        </section>
      )}

      {/* NEWSLETTER CTA */}
      <section className="hm-news container">
        <div className="hm-news__box">
          <h3>Suscribite y recibí novedades</h3>
          <p>Promos, lanzamientos y colecciones exclusivas.</p>
          <div className="input-button-group">
            <div className="input-group">
              <input
                className="email-input"
                onChange={(event) => setNewsletter(event.target.value)}
                type="email"
                placeholder="Tu email aquí..."
                id="email"
              />
              <div className="input-group-append">
                <button 
                  className="btn btn-primary-outline newsletter-btn" 
                  onClick={submitNewsletter} 
                  type="button"
                >
                  ENVIAR
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeMan;
